import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Copy, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, money, PAY_METHODS } from "@/lib/api";

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
};

const CopyHandle = ({ handle, testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await copyText(handle);
    setDone(true);
    toast.success(`Copied ${handle}`);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button type="button" data-testid={testId} onClick={copy} aria-label={`Copy ${handle}`} className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 transition-colors duration-200 hover:border-violet-400 hover:text-white">
      {done ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copied" : "Copy"}
    </button>
  );
};

const round2 = (n) => Math.round(n * 100) / 100;

const DropPick = ({ d, due, checked, disabled, onToggle }) => (
  <label data-testid={`payment-drop-option-${d.id}`} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${checked ? "border-violet-500/50 bg-violet-500/10" : "border-zinc-800 bg-zinc-950"} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:border-zinc-600"}`}>
    <Checkbox data-testid={`payment-drop-check-${d.id}`} checked={checked} disabled={disabled} onCheckedChange={onToggle} />
    <span className="flex-1 min-w-0 break-words leading-snug">{d.label}</span>
    {d.special && <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">Special</span>}
    <span data-testid={`payment-drop-due-${d.id}`} className={`font-mono text-xs shrink-0 ${due > 0 ? "text-pink-300" : "text-zinc-500"}`}>{due > 0 ? money(due) : "Nothing owed"}</span>
  </label>
);

export default function PaymentForm({ joinerId, dropBalances = [], specialBalances = [], initialDrop = "", onDone }) {
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [picked, setPicked] = useState([]);
  const [methods, setMethods] = useState([]);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drops, setDrops] = useState([]);
  useEffect(() => {
    const claimed = new Set(specialBalances.map((s) => s.special_drop_id));
    Promise.all([api.get("/drops"), api.get("/special-drops")]).then(([r, s]) => setDrops([
      ...r.data.filter((d) => d.status === "collecting_initials" || (d.status === "open" && d.collected_sets?.length && dropBalances.some((b) => b.drop_id === d.id))).map((d) => ({ ...d, key: `d:${d.id}`, label: `${d.store || d.title} · ${d.round_type} · ${d.album_type}` })),
      ...s.data.filter((d) => d.status === "collecting_initials" && claimed.has(d.id)).map((d) => ({ ...d, key: `s:${d.id}`, special: true, label: `${d.name} · Special Drop` })),
    ])).catch(() => {});
  }, [specialBalances, dropBalances]);
  const dueOf = (d) => {
    const b = d.special ? specialBalances.find((s) => s.special_drop_id === d.id) : dropBalances.find((x) => x.drop_id === d.id);
    return b ? Math.max(0, round2(b.balance - b.pending)) : 0;
  };
  const sel = drops.filter((d) => picked.includes(d.key));
  const owedDrops = drops.filter((d) => dueOf(d) > 0);
  const drop = sel[0];
  const payTo = drop ? drop.pay_to || "ThePastelFix" : null;
  const options = drop ? PAY_METHODS[payTo] : [];
  const multi = sel.length > 1;
  const total = round2(sel.reduce((s, d) => s + dueOf(d), 0));
  const choose = (keys) => {
    setPicked(keys);
    if (!keys.length) setMethods([]);
    const t = round2(drops.filter((d) => keys.includes(d.key)).reduce((s, d) => s + dueOf(d), 0));
    setAmount(t > 0 ? String(t) : "");
  };
  const togglePick = (key) => choose(picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key]);
  const owedGroups = Object.entries(drops.reduce((g, d) => {
    if (dueOf(d) > 0) (g[d.pay_to || "ThePastelFix"] ||= []).push(d);
    return g;
  }, {}));
  useEffect(() => {
    const keys = [].concat(initialDrop || []).filter((k) => drops.some((d) => d.key === k));
    if (keys.length) choose(keys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drops, initialDrop]);
  const single = !multi && drop ? (drop.special ? specialBalances.find((s) => s.special_drop_id === drop.id) : dropBalances.find((x) => x.drop_id === drop.id)) : null;
  const toggleMethod = (m) => setMethods((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  const submit = async (e) => {
    e.preventDefault();
    if (!sel.length) return toast.error("Choose the drop you're paying for");
    if (multi && total <= 0) return toast.error("Nothing is owed for the selected drops");
    if (!methods.length) return toast.error("Tick the payment method you used");
    if (!file) return toast.error("Attach your payment screenshot");
    const fd = new FormData();
    fd.append("joiner_id", joinerId);
    fd.append("amount", multi ? total : amount);
    fd.append("reference", reference);
    sel.forEach((d) => fd.append(d.special ? "special_drop_ids" : "drop_ids", d.id));
    methods.forEach((m) => fd.append("methods", m));
    fd.append("note", note);
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/payments", fd);
      toast.success(r.data.batch_size > 1 ? `Payment for ${r.data.batch_size} drops submitted — waiting for admin review` : "Payment submitted — waiting for admin review");
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't submit payment"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-[1fr_320px] gap-6 rounded-2xl border border-white/5 bg-zinc-900/60 p-5 sm:p-7" data-testid="payment-form">
      <div className="space-y-4 min-w-0">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="amount">{multi ? `Total owed for ${sel.length} drops` : "Amount paid"}</Label>
            <Input id="amount" data-testid="payment-amount-input" type="number" step="0.01" min="0.01" required readOnly={multi} value={multi ? String(total) : amount} onChange={(e) => setAmount(e.target.value)} className={`bg-zinc-950 font-mono ${multi ? "text-pink-300 cursor-not-allowed" : ""}`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reference">Reference / transaction ID</Label>
            <Input id="reference" data-testid="payment-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} className="bg-zinc-950" />
          </div>
        </div>
        <div className="space-y-1.5" data-testid="payment-drop-list">
          <Label>For drop(s) — tick one or more</Label>
          {owedDrops.length === 0 && <p data-testid="payment-no-drops" className="text-sm text-zinc-500">Nothing is owed on any drop collecting initials right now</p>}
          {owedGroups.length > 0 && (
            <div className="flex flex-wrap gap-2 pb-1">
              {owedGroups.map(([to, list]) => (
                <button key={to} type="button" data-testid={`payment-pay-all-${to}`} onClick={() => choose(list.map((d) => d.key))}
                  className="rounded-full bg-pink-500 px-3.5 py-1.5 text-xs font-semibold text-white transition-[transform,background-color] duration-200 hover:bg-pink-400 hover:-translate-y-0.5">
                  Pay everything owed{owedGroups.length > 1 ? ` to ${to}` : ""} · {money(round2(list.reduce((s, d) => s + dueOf(d), 0)))}
                </button>
              ))}
            </div>
          )}
          {owedGroups.length > 1 && <p className="text-[11px] text-zinc-500">Your drops go to different payment destinations, so pay each one separately.</p>}
          <div className="space-y-1.5">
            {owedDrops.map((d) => (
              <DropPick key={d.key} d={d} due={dueOf(d)} checked={picked.includes(d.key)} onToggle={() => togglePick(d.key)}
                disabled={!!payTo && (d.pay_to || "ThePastelFix") !== payTo && !picked.includes(d.key)} />
            ))}
          </div>
          {payTo && owedDrops.some((d) => (d.pay_to || "ThePastelFix") !== payTo) && <p className="text-[11px] text-zinc-500">Only drops paid to {payTo} can be combined in this payment.</p>}
          {multi && (
            <p data-testid="payment-multi-total" className="text-xs text-pink-200/80">
              {sel.map((d) => `${d.label.split(" · ")[0]} ${money(dueOf(d))}`).join(" + ")} = <span className="font-mono font-semibold">{money(total)}</span>
            </p>
          )}
          {single && (
            <p data-testid="payment-single-due" className="text-xs text-zinc-400">
              {drop.special ? "Special drop · " : ""}Owed {money(single.owed)} · approved {money(single.paid)}{single.pending ? ` · pending ${money(single.pending)}` : ""} · left to pay <span className="font-mono text-pink-300">{money(dueOf(drop))}</span>
            </p>
          )}
        </div>
        {drop && (
          <div className="space-y-2 rounded-xl border border-zinc-800 p-4" data-testid="payment-methods">
            <Label>Send payments to {payTo} — tick the method you used</Label>
            {options.map((m, i) => {
              const handle = m.split(": ")[1];
              return (
                <div key={m} className="flex items-center gap-3 text-sm">
                  <label className="flex flex-1 items-center gap-3 cursor-pointer min-w-0">
                    <Checkbox data-testid={`payment-method-${i}`} checked={methods.includes(m)} onCheckedChange={() => toggleMethod(m)} />
                    <span className="break-all">{m}</span>
                  </label>
                  {handle && !/message for info/i.test(handle) && <CopyHandle handle={handle} testId={`payment-method-copy-${i}`} />}
                </div>
              );
            })}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="note">Note (optional)</Label>
          <Textarea id="note" data-testid="payment-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="bg-zinc-950" rows={3} />
        </div>
        <Button type="submit" disabled={busy} data-testid="payment-submit-button" className="h-11 px-6 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 hover:opacity-90 text-white font-semibold">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit payment
        </Button>
      </div>
      <label className="relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-zinc-700 hover:border-violet-500/60 transition-colors cursor-pointer min-h-[240px] overflow-hidden bg-zinc-950">
        <input type="file" accept="image/*" className="hidden" data-testid="payment-screenshot-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {preview ? (
          <img src={preview} alt="Payment screenshot preview" data-testid="payment-screenshot-preview" className="absolute inset-0 h-full w-full object-contain" />
        ) : (
          <div className="text-center p-6">
            <ImagePlus className="h-8 w-8 mx-auto text-zinc-500" />
            <div className="mt-2 text-sm font-medium">Upload screenshot</div>
            <div className="text-xs text-zinc-500 mt-1">PNG, JPG or WEBP · up to 8 MB</div>
          </div>
        )}
      </label>
    </form>
  );
}
